'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  quote: string;
  metrics: {
    label: string;
    value: string;
  }[];
}

const SocialProofCarousel = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Prasanna",
    role: "Chairman",
    company: "GPT INC",
    image: "/assets/images/no_image.png",
    alt: "Professional business leader",
    quote: "Invictosoft transformed our data infrastructure in just 3 months. Their team didn't just consult - they rolled up their sleeves and delivered a production-ready solution that handles 10x our previous data volume.",
    metrics: [
    { label: "Time to Production", value: "3 Months" },
    { label: "Data Processing", value: "10x Increase" },
    { label: "Cost Reduction", value: "40%" }]

  },
  {
    id: 2,
    name: "Swathi",
    role: "Chief of Staff",
    company: "Samiteon LLC",
    image: "/assets/images/no_image.png",
    alt: "Professional business leader",
    quote: "Working with Invictosoft felt like collaborating with an extension of our own team. They combine technical strength with a clear understanding of business priorities.",
    metrics: [
    { label: "Onboarding Time", value: "2 Weeks" },
    { label: "Launch Acceleration", value: "6 Weeks Early" },
    { label: "Team Productivity", value: "+65%" }]

  },
  {
    id: 3,
    name: "Darshan",
    role: "Director of Operations",
    company: "Dyumath Technologies",
    image: "/assets/images/no_image.png",
    alt: "Professional business leader",
    quote: "Their AI agents automated our customer support workflow, reducing response time from 4 hours to 15 minutes while maintaining 95% customer satisfaction. The ROI was evident within the first month.",
    metrics: [
    { label: "Response Time", value: "94% Faster" },
    { label: "Customer Satisfaction", value: "95%" },
    { label: "ROI Timeline", value: "1 Month" }]

  },
  {
    id: 4,
    name: "Nithin",
    role: "Chief Information Officer",
    company: "Sindoor HealthTech",
    image: "/assets/images/no_image.png",
    alt: "Professional business leader",
    quote: "Invictosoft' expertise in both Microsoft and AWS ecosystems was invaluable during our cloud migration. They delivered a hybrid solution that reduced our infrastructure costs by 55% while improving performance.",
    metrics: [
    { label: "Cost Savings", value: "55%" },
    { label: "Performance Gain", value: "+80%" },
    { label: "Migration Time", value: "4 Months" }]

  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handlePrevious = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handleDotClick = (index: number) => {
    if (!isHydrated) return;
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground font-body max-w-3xl mx-auto">
            Real results from real partnerships. See how we've helped businesses transform their technology operations.
          </p>
        </div>

        <div className="relative">
          <div className="bg-card rounded-2xl shadow-partnership border border-border overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 p-8 sm:p-12">
              <div className="flex flex-col justify-center space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-primary">
                    {isHydrated ?
                    <AppImage
                      src={currentTestimonial.image}
                      alt={currentTestimonial.alt}
                      width={64}
                      height={64}
                      className="w-full h-full object-cover" /> :


                    <AppImage
                      src={testimonials[0].image}
                      alt={testimonials[0].alt}
                      width={64}
                      height={64}
                      className="w-full h-full object-cover" />

                    }
                  </div>
                  <div>
                    <h3 className="text-xl font-headline font-bold text-foreground">
                      {isHydrated ? currentTestimonial.name : testimonials[0].name}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body">
                      {isHydrated ? currentTestimonial.role : testimonials[0].role}
                    </p>
                    <p className="text-sm text-primary font-body font-semibold">
                      {isHydrated ? currentTestimonial.company : testimonials[0].company}
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <Icon name="ChatBubbleLeftIcon" size={40} variant="solid" className="text-muted opacity-20 absolute -top-2 -left-2" />
                  <p className="text-base sm:text-lg text-foreground font-body leading-relaxed relative z-10 pl-8">
                    "{isHydrated ? currentTestimonial.quote : testimonials[0].quote}"
                  </p>
                </div>

                <div className="flex items-center space-x-4 pt-4">
                  <button
                    onClick={handlePrevious}
                    className="w-10 h-10 flex items-center justify-center bg-muted hover:bg-primary hover:text-primary-foreground rounded-full transition-all duration-base"
                    aria-label="Previous testimonial">

                    <Icon name="ChevronLeftIcon" size={20} variant="outline" />
                  </button>
                  <div className="flex items-center space-x-2">
                    {testimonials.map((_, index) =>
                    <button
                      key={index}
                      onClick={() => handleDotClick(index)}
                      className={`h-2 rounded-full transition-all duration-base ${
                      isHydrated && currentIndex === index ?
                      'w-8 bg-primary' : 'w-2 bg-muted-foreground hover:bg-primary'}`
                      }
                      aria-label={`Go to testimonial ${index + 1}`} />

                    )}
                  </div>
                  <button
                    onClick={handleNext}
                    className="w-10 h-10 flex items-center justify-center bg-muted hover:bg-primary hover:text-primary-foreground rounded-full transition-all duration-base"
                    aria-label="Next testimonial">

                    <Icon name="ChevronRightIcon" size={20} variant="outline" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="grid grid-cols-3 gap-4 w-full">
                  {(isHydrated ? currentTestimonial.metrics : testimonials[0].metrics).map((metric, index) =>
                  <div
                    key={index}
                    className="bg-muted rounded-lg p-4 text-center">

                      <div className="text-2xl sm:text-3xl font-headline font-bold text-primary mb-1">
                        {metric.value}
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground font-body">
                        {metric.label}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default SocialProofCarousel;